/**
 * Role 11 — Sub-classification (Liquidity) — calculated totals.
 *
 * Extracted verbatim from the former single-class `CalculationService`, which held
 * all 13 sheets' total calculations in one 5,557-line file. Each function is pure and
 * self-contained (no shared state, no `this`), so the split is a move, not a rewrite.
 * `CalculationService` remains the injectable entry point and delegates here.
 */

/**
 * Calculate all Role11 (220100 - Sub-classification, Liquidity ordering) totals.
 *
 * Ten subtotal rows: PPE, Intangibles, Inventories, Trade Receivables net,
 * Other Receivables, Trade Payables, Other Payables, Borrowings, Share Capital,
 * Other Reserves. Trade Receivables uses gross − allowance; the rest are pure
 * sums.
 *
 * @param formData - The form data object (control name → value)
 * @param suffixes - Resolved column suffixes
 * @returns Object containing only the calculated total fields
 */
export function calculateTotals_220100(
  formData: Record<string, any>,
  suffixes: ReadonlyArray<string>,
): Record<string, any> {
  const calculatedTotals: Record<string, any> = {};

  const num = (key: string): number => {
    const raw = formData[key];
    if (raw === null || raw === undefined || raw === '') return 0;
    const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  for (const suffix of suffixes) {
    // 1. Property, Plant and Equipment subtotal
    calculatedTotals[
      `ssmt:PropertyPlantAndEquipmentSubClassificationLiquidity${suffix}`
    ] =
      num(`ssmt:LandAndBuildingsLiquidity${suffix}`) +
      num(`ssmt:PlantAndMachineryLiquidity${suffix}`) +
      num(`ssmt:MotorVehiclesLiquidity${suffix}`) +
      num(`ssmt:FurnitureFixturesAndFittingsLiquidity${suffix}`) +
      num(`ssmt:OfficeEquipmentLiquidity${suffix}`) +
      num(`ssmt:CapitalWorkInProgressLiquidity${suffix}`);

    // 2. Intangible Assets subtotal
    calculatedTotals[
      `ssmt:IntangibleAssetsSubClassificationLiquidity${suffix}`
    ] =
      num(`ssmt:ComputerSoftwareLiquidity${suffix}`) +
      num(`ssmt:GoodwillLiquidity${suffix}`) +
      num(`ssmt:BrandNamesLiquidity${suffix}`) +
      num(`ssmt:OtherIntangibleAssetsLiquidity${suffix}`);

    // 3. Inventories subtotal
    calculatedTotals[`ssmt:InventoriesSubClassificationLiquidity${suffix}`] =
      num(`ssmt:RawMaterialsLiquidity${suffix}`) +
      num(`ssmt:WorkInProgressLiquidity${suffix}`) +
      num(`ssmt:FinishedGoodsLiquidity${suffix}`) +
      num(`ssmt:ConsumablesAndSparesLiquidity${suffix}`);

    // 4. Trade Receivables net (gross − allowance)
    calculatedTotals[`ssmt:TradeReceivablesNetAmountLiquidity${suffix}`] =
      num(`ssmt:TradeReceivablesGrossAmountLiquidity${suffix}`) -
      num(`ssmt:AllowanceForDoubtfulDebtsLiquidity${suffix}`);

    // 5. Other Receivables subtotal
    calculatedTotals[
      `ssmt:OtherReceivablesSubClassificationLiquidity${suffix}`
    ] =
      num(`ssmt:DepositsLiquidity${suffix}`) +
      num(`ssmt:PrepaymentsLiquidity${suffix}`) +
      num(`ssmt:AccruedIncomeLiquidity${suffix}`);

    // 6. Trade Payables subtotal
    calculatedTotals[
      `ssmt:TradePayablesSubClassificationLiquidity${suffix}`
    ] =
      num(`ssmt:TradePayablesDomesticLiquidity${suffix}`) +
      num(`ssmt:TradePayablesForeignLiquidity${suffix}`);

    // 7. Other Payables subtotal
    calculatedTotals[
      `ssmt:OtherPayablesSubClassificationLiquidity${suffix}`
    ] =
      num(`ssmt:AccruedExpensesLiquidity${suffix}`) +
      num(`ssmt:DepositsReceivedLiquidity${suffix}`) +
      num(`ssmt:SundryPayablesLiquidity${suffix}`);

    // 8. Borrowings subtotal
    calculatedTotals[`ssmt:BorrowingsSubClassificationLiquidity${suffix}`] =
      num(`ssmt:BankOverdraftsSubClassificationLiquidity${suffix}`) +
      num(`ssmt:TermLoansLiquidity${suffix}`) +
      num(`ssmt:RevolvingCreditLiquidity${suffix}`) +
      num(`ssmt:OtherBorrowingsLiquidity${suffix}`);

    // 9. Share Capital subtotal
    calculatedTotals[`ssmt:ShareCapitalSubClassificationLiquidity${suffix}`] =
      num(`ssmt:OrdinarySharesLiquidity${suffix}`) +
      num(`ssmt:PreferenceSharesLiquidity${suffix}`);

    // 10. Other Reserves subtotal
    calculatedTotals[
      `ssmt:OtherReservesSubClassificationLiquidity${suffix}`
    ] =
      num(`ssmt:RevaluationSurplusLiquidity${suffix}`) +
      num(`ssmt:ForeignCurrencyTranslationReserveLiquidity${suffix}`) +
      num(`ssmt:OtherCapitalReservesLiquidity${suffix}`);
  }

  return calculatedTotals;
}
